/* Umya — carrusel de piezas. 19-ago-2026.
 *
 * Sustituye al slider-component de Dawn, que solo daba dos flechas pequenas
 * debajo y un contador que contaba posiciones de scroll en vez de piezas.
 *
 * Lo que hace de mas:
 *   - flechas grandes a los lados, a la altura de la foto, que se apagan solas
 *     en los extremos;
 *   - barra de avance arrastrable: dice cuanto hay y donde estas, que es lo
 *     que el "1 / 9" no decia;
 *   - arrastre con el raton en escritorio (agarrar y tirar), cancelando el
 *     enlace si ha habido movimiento real;
 *   - avance automatico opcional, que se para al pasar el raton, al enfocar
 *     con teclado, cuando el bloque no esta en pantalla, y PARA SIEMPRE en
 *     cuanto la persona toca algo: el gesto humano manda sobre el reloj;
 *   - respeta "prefers-reduced-motion": ahi no arranca solo nunca.
 */
class UmyaCarrusel extends HTMLElement {
  connectedCallback() {
    this.pista = this.querySelector('[data-pista]');
    if (!this.pista) return;

    this.prev = this.querySelector('[data-prev]');
    this.next = this.querySelector('[data-next]');
    this.rail = this.querySelector('[data-rail]');
    this.pastilla = this.querySelector('[data-pastilla]');

    this.quieto = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    this.auto = this.dataset.auto === 'true' && !this.quieto;
    this.espera = (parseFloat(this.dataset.segundos) || 5) * 1000;
    this.tocado = false;
    this.enPantalla = false;
    this.raton = false;

    this.pintar = this.pintar.bind(this);
    this.pista.addEventListener('scroll', this.pintar, { passive: true });
    window.addEventListener('resize', this.pintar);

    this.prev?.addEventListener('click', () => this.mover(-1));
    this.next?.addEventListener('click', () => this.mover(1));

    this.montarSegundaFoto();
    this.montarArrastre();
    this.montarBarra();
    this.montarTeclado();
    this.montarAuto();
    this.alinearFlechas();

    requestAnimationFrame(this.pintar);
  }

  /* Las flechas van a media altura de la FOTO, no de la tarjeta: el nombre y
   * el precio de abajo tiran el centro geometrico y quedaban por debajo de la
   * imagen. Se mide en vivo porque el alto depende del ancho de pantalla. */
  alinearFlechas() {
    const marco = this.querySelector('[data-marco]');
    const foto = this.querySelector('.umya-pieza__media');
    if (!marco || !foto) return;
    const poner = () => marco.style.setProperty('--umya-media-medio', foto.getBoundingClientRect().height / 2 + 'px');
    poner();
    if ('ResizeObserver' in window) new ResizeObserver(poner).observe(foto);
    else window.addEventListener('resize', poner);
  }

  /* La segunda foto de cada pieza nace sin src y se baja en el primer roce del
   * raton (o al llegar con el tabulador). Doce fotos mas en la portada no son
   * gratis, y en movil no las ve nadie porque alli no hay raton. */
  montarSegundaFoto() {
    const bajar = (img) => {
      if (!img || !img.hasAttribute('data-tarde')) return;
      // La clase la pone la carga, no el hover: si el cambio se hiciera antes
      // de tener la foto, el primer paso del raton enseñaria un hueco blanco.
      img.addEventListener('load', () => img.classList.add('esta-lista'), { once: true });
      img.srcset = img.dataset.srcset;
      img.src = img.dataset.src;
      img.removeAttribute('data-tarde');
    };

    // Al entrar el raton en el bloque se piden todas de golpe: da el margen
    // justo para que la primera pieza que se roce ya la tenga.
    this.addEventListener(
      'mouseenter',
      () => this.querySelectorAll('.umya-pieza__foto--dos[data-tarde]').forEach(bajar),
      { once: true }
    );
    this.addEventListener('mouseover', (e) => bajar(e.target.closest?.('.umya-pieza')?.querySelector('.umya-pieza__foto--dos[data-tarde]')));
    this.addEventListener('focusin', (e) => bajar(e.target.closest?.('.umya-pieza')?.querySelector('.umya-pieza__foto--dos[data-tarde]')));
  }

  /* ---- medidas ---- */

  get paso() {
    const uno = this.pista.querySelector('[data-hueco]');
    if (!uno) return 300;
    const gap = parseFloat(getComputedStyle(this.pista).columnGap) || 0;
    return uno.getBoundingClientRect().width + gap;
  }

  get visibles() {
    return Math.max(1, Math.floor(this.pista.clientWidth / this.paso));
  }

  get tope() {
    return Math.max(0, this.pista.scrollWidth - this.pista.clientWidth);
  }

  /* ---- movimiento ---- */

  mover(signo, piezas) {
    // La flecha avanza una pantalla menos una pieza: la ultima que estabas
    // mirando se queda a la vista y no pierdes el hilo de la fila.
    const salto = piezas || Math.max(1, this.visibles - 1);
    this.pista.scrollBy({ left: signo * salto * this.paso, behavior: 'smooth' });
    this.parar();
  }

  pintar() {
    const x = this.pista.scrollLeft;
    const tope = this.tope;
    if (this.prev) this.prev.disabled = x <= 2;
    if (this.next) this.next.disabled = x >= tope - 2;
    if (this.pastilla) {
      const trozo = Math.min(1, this.pista.clientWidth / this.pista.scrollWidth);
      const avance = tope > 0 ? x / tope : 0;
      this.pastilla.style.width = (trozo * 100).toFixed(2) + '%';
      this.pastilla.style.left = (avance * (100 - trozo * 100)).toFixed(2) + '%';
    }
    this.rail?.setAttribute('aria-valuenow', String(Math.round(tope > 0 ? (x / tope) * 100 : 0)));
  }

  /* ---- gestos ---- */

  montarArrastre() {
    let activo = false;
    let inicioX = 0;
    let inicioScroll = 0;
    let recorrido = 0;

    this.pista.addEventListener('pointerdown', (e) => {
      // En tactil NO se para aqui: el dedo se apoya en la fila para bajar por
      // la pagina, y eso mataba el reloj sin que nadie hubiera tocado el
      // carrusel. Se para en el touchmove, y solo si el gesto es horizontal.
      if (e.pointerType !== 'mouse') return;
      activo = true;
      recorrido = 0;
      inicioX = e.clientX;
      inicioScroll = this.pista.scrollLeft;
      this.pista.classList.add('esta-agarrado');
    });

    this.pista.addEventListener('pointermove', (e) => {
      if (!activo) return;
      const delta = e.clientX - inicioX;
      recorrido = Math.abs(delta);
      if (recorrido > 4) {
        this.pista.scrollLeft = inicioScroll - delta;
        this.parar();
      }
    });

    const soltar = () => {
      if (!activo) return;
      activo = false;
      this.pista.classList.remove('esta-agarrado');
      // Si hubo arrastre de verdad, el click que viene detras abriria la ficha
      // sin querer: se anula solo esa vez.
      if (recorrido > 6) {
        const tragar = (ev) => {
          ev.preventDefault();
          ev.stopPropagation();
        };
        this.pista.addEventListener('click', tragar, { capture: true, once: true });
        setTimeout(() => this.pista.removeEventListener('click', tragar, { capture: true }), 60);
      }
    };
    this.pista.addEventListener('pointerup', soltar);
    this.pista.addEventListener('pointerleave', soltar);

    // ESTE era el fallo del avance automatico: la rueda paraba el reloj para
    // siempre, y para llegar a este bloque hay que bajar por la portada con la
    // rueda pasando el cursor justo por encima de la fila. Es decir: se mataba
    // solo, antes incluso de estar en pantalla. Ahora solo cuenta como gesto
    // sobre el carrusel el desplazamiento HORIZONTAL (trackpad de lado); el
    // vertical es de la pagina y no le incumbe.
    this.pista.addEventListener(
      'wheel',
      (e) => {
        if (Math.abs(e.deltaX) > Math.abs(e.deltaY)) this.parar();
      },
      { passive: true }
    );

    // Lo mismo con el dedo: solo para si arrastra de lado.
    let dedoX = 0;
    let dedoY = 0;
    this.pista.addEventListener(
      'touchstart',
      (e) => {
        dedoX = e.touches[0].clientX;
        dedoY = e.touches[0].clientY;
      },
      { passive: true }
    );
    this.pista.addEventListener(
      'touchmove',
      (e) => {
        const dx = Math.abs(e.touches[0].clientX - dedoX);
        const dy = Math.abs(e.touches[0].clientY - dedoY);
        if (dx > 10 && dx > dy) this.parar();
      },
      { passive: true }
    );
  }

  montarBarra() {
    if (!this.rail) return;
    const irA = (clientX) => {
      const caja = this.rail.getBoundingClientRect();
      const proporcion = Math.min(1, Math.max(0, (clientX - caja.left) / caja.width));
      this.pista.scrollLeft = proporcion * this.tope;
      this.parar();
    };
    let arrastrando = false;
    this.rail.addEventListener('pointerdown', (e) => {
      arrastrando = true;
      this.rail.setPointerCapture(e.pointerId);
      irA(e.clientX);
    });
    this.rail.addEventListener('pointermove', (e) => arrastrando && irA(e.clientX));
    this.rail.addEventListener('pointerup', () => (arrastrando = false));
    this.rail.addEventListener('pointercancel', () => (arrastrando = false));
  }

  montarTeclado() {
    this.addEventListener('keydown', (e) => {
      if (e.key === 'ArrowRight') {
        this.mover(1, 1);
        e.preventDefault();
      }
      if (e.key === 'ArrowLeft') {
        this.mover(-1, 1);
        e.preventDefault();
      }
    });
    // Al tabular hasta una pieza escondida, el navegador la trae; que eso
    // tambien pare el reloj.
    this.addEventListener('focusin', () => this.parar());
  }

  /* ---- avance automatico ---- */

  montarAuto() {
    if (!this.auto) return;

    // El raton congela la fila solo cuando esta sobre una PIEZA, no sobre el
    // bloque entero: si basta con tener el cursor parado en cualquier margen
    // para que no se mueva nada, parece que el avance no funciona (que es
    // justo lo que se veia).
    this.addEventListener('mouseover', (e) => {
      const sobrePieza = !!e.target.closest?.('.umya-pieza');
      if (sobrePieza === this.raton) return;
      this.raton = sobrePieza;
      sobrePieza ? this.pausar() : this.arrancar();
    });
    this.addEventListener('mouseleave', () => {
      this.raton = false;
      this.arrancar();
    });
    document.addEventListener('visibilitychange', () => (document.hidden ? this.pausar() : this.arrancar()));

    // Solo corre mientras el bloque esta a la vista: nada de mover cosas que
    // nadie esta mirando.
    if ('IntersectionObserver' in window) {
      new IntersectionObserver(
        (entradas) => {
          this.enPantalla = entradas[0].isIntersecting;
          this.enPantalla ? this.arrancar() : this.pausar();
        },
        { threshold: 0.35 }
      ).observe(this);
    } else {
      this.enPantalla = true;
      this.arrancar();
    }
  }

  arrancar() {
    if (!this.auto || this.tocado || this.raton || !this.enPantalla || document.hidden) return;
    this.pausar();
    this.reloj = setInterval(() => {
      if (this.pista.scrollLeft >= this.tope - 2) {
        this.pista.scrollTo({ left: 0, behavior: 'smooth' });
      } else {
        this.pista.scrollBy({ left: this.paso, behavior: 'smooth' });
      }
    }, this.espera);
  }

  pausar() {
    clearInterval(this.reloj);
  }

  /** Un gesto humano manda: el reloj no vuelve a arrancar en toda la visita. */
  parar() {
    this.tocado = true;
    this.pausar();
  }
}

customElements.define('umya-carrusel', UmyaCarrusel);
